import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  MessageSquare,
  Calendar,
  Target,
  Bell,
  CheckCircle,
  AlertTriangle,
  Clock,
  FileText,
  TrendingUp,
  Award,
  BookOpen,
  User,
  UserCheck,
  Video,
  Phone,
  Mail,
  Share2,
  Download,
  Upload,
  Eye,
  Edit3,
  Plus,
  Filter,
  Search
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface FamilyMember {
  id: string;
  name: string;
  role: 'student' | 'parent' | 'guardian' | 'counselor';
  email: string;
  phone: string;
  avatar: string;
  lastActive: string;
  permissions: string[];
}

interface Message {
  id: string;
  senderId: string;
  content: string;
  timestamp: string;
  type: 'text' | 'file' | 'meeting' | 'update';
  attachments?: string[];
  priority: 'low' | 'medium' | 'high';
  read: boolean;
}

interface Task {
  id: string;
  title: string;
  description: string;
  assignedTo: string[];
  dueDate: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'completed' | 'overdue';
  category: 'academic' | 'application' | 'research' | 'testing' | 'planning';
  progress: number;
  comments: number;
}

interface SharedDocument {
  id: string;
  name: string;
  type: 'essay' | 'application' | 'transcript' | 'recommendation' | 'schedule' | 'report';
  sharedBy: string;
  sharedWith: string[];
  lastModified: string;
  size: string;
  permissions: 'view' | 'edit' | 'comment';
}

interface Meeting {
  id: string;
  title: string;
  participants: string[];
  date: string;
  time: string;
  duration: number;
  type: 'video' | 'phone' | 'in-person';
  agenda: string[];
  status: 'scheduled' | 'completed' | 'cancelled';
  notes?: string;
}

const familyMembers: FamilyMember[] = [
  {
    id: 'student-1',
    name: 'Sarah Chen',
    role: 'student',
    email: 'sarah.chen@email.com',
    phone: '(555) 123-4567',
    avatar: '👧🏻',
    lastActive: '2 hours ago',
    permissions: ['view-all', 'edit-profile', 'submit-work']
  },
  {
    id: 'parent-1',
    name: 'Lisa Chen',
    role: 'parent',
    email: 'lisa.chen@email.com',
    phone: '(555) 234-5678',
    avatar: '👩🏻‍💼',
    lastActive: '1 hour ago',
    permissions: ['view-all', 'communicate', 'schedule-meetings', 'approve-applications']
  },
  {
    id: 'parent-2',
    name: 'David Chen',
    role: 'parent',
    email: 'david.chen@email.com',
    phone: '(555) 345-6789',
    avatar: '👨🏻‍💼',
    lastActive: '4 hours ago',
    permissions: ['view-progress', 'communicate', 'financial-planning']
  },
  {
    id: 'counselor-1',
    name: 'Ms. Rodriguez',
    role: 'counselor',
    email: 'a.rodriguez@school.edu',
    phone: '(555) 456-7890',
    avatar: '👩🏽‍🏫',
    lastActive: '30 minutes ago',
    permissions: ['view-all', 'edit-plans', 'schedule-meetings', 'submit-recommendations']
  }
];

const recentMessages: Message[] = [
  {
    id: '1',
    senderId: 'counselor-1',
    content: 'Sarah\'s midterm grades are excellent! Her GPA is now 3.9. I recommend considering early decision for her top choice schools.',
    timestamp: '2 hours ago',
    type: 'update',
    priority: 'high',
    read: false
  },
  {
    id: '2',
    senderId: 'parent-1',
    content: 'Can we schedule a meeting to discuss college application timeline? I want to make sure we\'re on track.',
    timestamp: '4 hours ago',
    type: 'text',
    priority: 'medium',
    read: true
  },
  {
    id: '3',
    senderId: 'student-1',
    content: 'I completed my personal statement draft. Please review when you have time.',
    timestamp: '1 day ago',
    type: 'file',
    attachments: ['personal-statement-draft-v2.pdf'],
    priority: 'medium',
    read: true
  }
];

const activeTasks: Task[] = [
  {
    id: '1',
    title: 'Complete Common Application Essay',
    description: 'Finalize personal statement and supplemental essays for Common App',
    assignedTo: ['student-1'],
    dueDate: '2024-10-15',
    priority: 'high',
    status: 'in-progress',
    category: 'application',
    progress: 75,
    comments: 3
  },
  {
    id: '2',
    title: 'Request Teacher Recommendations',
    description: 'Ask Ms. Johnson and Mr. Thompson for recommendation letters',
    assignedTo: ['student-1', 'counselor-1'],
    dueDate: '2024-10-01',
    priority: 'high',
    status: 'completed',
    category: 'application',
    progress: 100,
    comments: 1
  },
  {
    id: '3',
    title: 'SAT Subject Test Registration',
    description: 'Register for Math Level 2 and Chemistry subject tests',
    assignedTo: ['parent-1', 'student-1'],
    dueDate: '2024-09-30',
    priority: 'medium',
    status: 'overdue',
    category: 'testing',
    progress: 0,
    comments: 2
  }
];

const sharedDocuments: SharedDocument[] = [
  {
    id: '1',
    name: 'Personal Statement Draft v2',
    type: 'essay',
    sharedBy: 'student-1',
    sharedWith: ['parent-1', 'parent-2', 'counselor-1'],
    lastModified: '2 hours ago',
    size: '2.4 MB',
    permissions: 'edit'
  },
  {
    id: '2',
    name: 'College Application Timeline',
    type: 'schedule',
    sharedBy: 'counselor-1',
    sharedWith: ['student-1', 'parent-1', 'parent-2'],
    lastModified: '1 day ago',
    size: '1.2 MB',
    permissions: 'view'
  },
  {
    id: '3',
    name: 'Financial Aid Worksheet',
    type: 'application',
    sharedBy: 'parent-1',
    sharedWith: ['parent-2', 'counselor-1'],
    lastModified: '3 days ago',
    size: '856 KB',
    permissions: 'edit'
  }
];

const upcomingMeetings: Meeting[] = [
  {
    id: '1',
    title: 'College Application Strategy Session',
    participants: ['student-1', 'parent-1', 'counselor-1'],
    date: '2024-09-25',
    time: '3:00 PM',
    duration: 60,
    type: 'video',
    agenda: ['Review application timeline', 'Discuss college list', 'Plan essay topics'],
    status: 'scheduled'
  },
  {
    id: '2',
    title: 'Financial Aid Planning',
    participants: ['parent-1', 'parent-2', 'counselor-1'],
    date: '2024-09-28',
    time: '7:00 PM',
    duration: 45,
    type: 'video',
    agenda: ['FAFSA preparation', 'Scholarship opportunities', 'Cost comparison'],
    status: 'scheduled'
  }
];

export const ParentCounselorDashboard: React.FC = () => {
  const [selectedView, setSelectedView] = useState<'overview' | 'messages' | 'tasks' | 'documents' | 'meetings'>('overview');
  const [selectedRole, setSelectedRole] = useState<'all' | 'student' | 'parent' | 'counselor'>('all');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in-progress': return 'text-blue-600 bg-blue-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      default: return 'text-green-600';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'student': return BookOpen;
      case 'parent': return Users;
      case 'counselor': return UserCheck;
      default: return User;
    }
  };

  const filteredMembers = selectedRole === 'all'
    ? familyMembers
    : familyMembers.filter(member => member.role === selectedRole);

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <Users className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Family Collaboration Dashboard</h2>
          <p className="text-primary-100 text-lg mb-6">
            Seamless communication and coordination between students, parents, and counselors
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{familyMembers.length}</div>
              <div className="text-sm text-primary-100">Active Members</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{activeTasks.filter(t => t.status !== 'completed').length}</div>
              <div className="text-sm text-primary-100">Pending Tasks</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{upcomingMeetings.length}</div>
              <div className="text-sm text-primary-100">Upcoming Meetings</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Navigation Tabs */}
      <Card>
        <div className="flex border-b border-gray-200 mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'messages', label: 'Messages', icon: MessageSquare },
            { id: 'tasks', label: 'Tasks', icon: Target },
            { id: 'documents', label: 'Documents', icon: FileText },
            { id: 'meetings', label: 'Meetings', icon: Calendar }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setSelectedView(id as any)}
              className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                selectedView === id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              <span>{label}</span>
              {id === 'messages' && recentMessages.filter(m => !m.read).length > 0 && (
                <Badge variant="warning" size="sm">
                  {recentMessages.filter(m => !m.read).length}
                </Badge>
              )}
            </button>
          ))}
        </div>

        {/* Role Filter */}
        <div className="flex items-center space-x-4 mb-6">
          <span className="text-sm font-medium text-gray-700">Filter by role:</span>
          <div className="flex space-x-2">
            {['all', 'student', 'parent', 'counselor'].map((role) => (
              <button
                key={role}
                onClick={() => setSelectedRole(role as any)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  selectedRole === role
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Content Based on Selected View */}
      <AnimatePresence mode="wait">
        {selectedView === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            {/* Family Members */}
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Family & Support Team</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredMembers.map((member, index) => {
                  const RoleIcon = getRoleIcon(member.role);
                  return (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="text-3xl">{member.avatar}</div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-gray-900">{member.name}</h4>
                          <RoleIcon size={16} className="text-gray-500" />
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{member.email}</p>
                        <p className="text-xs text-gray-500">Last active: {member.lastActive}</p>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="ghost" size="sm">
                          <MessageSquare size={14} />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Video size={14} />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </Card>

            {/* Quick Actions & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Quick Actions */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button className="w-full justify-start" variant="outline">
                    <Calendar className="mr-2" size={16} />
                    Schedule Family Meeting
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Upload className="mr-2" size={16} />
                    Upload Document
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Plus className="mr-2" size={16} />
                    Create Task
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Share2 className="mr-2" size={16} />
                    Share Progress Report
                  </Button>
                </div>
              </Card>

              {/* Recent Activity */}
              <Card>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {recentMessages.slice(0, 4).map((message, index) => (
                    <div key={message.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-xl">
                        {familyMembers.find(m => m.id === message.senderId)?.avatar}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {familyMembers.find(m => m.id === message.senderId)?.name}
                          </span>
                          <span className="text-xs text-gray-500">{message.timestamp}</span>
                          {!message.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 line-clamp-2">{message.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </motion.div>
        )}

        {selectedView === 'tasks' && (
          <motion.div
            key="tasks"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Active Tasks</h3>
                <Button size="sm">
                  <Plus size={16} className="mr-2" />
                  New Task
                </Button>
              </div>

              <div className="space-y-4">
                {activeTasks.map((task, index) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border border-gray-200 rounded-xl p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-lg font-semibold text-gray-900">{task.title}</h4>
                          <Badge className={getStatusColor(task.status)} size="sm">
                            {task.status.replace('-', ' ')}
                          </Badge>
                          <span className={`text-sm font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority} priority
                          </span>
                        </div>
                        <p className="text-gray-600 mb-3">{task.description}</p>

                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center space-x-1">
                            <Clock size={14} />
                            <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users size={14} />
                            <span>{task.assignedTo.length} assigned</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MessageSquare size={14} />
                            <span>{task.comments} comments</span>
                          </div>
                        </div>

                        {task.status !== 'completed' && (
                          <div className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">Progress</span>
                              <span className="text-sm font-semibold text-gray-900">{task.progress}%</span>
                            </div>
                            <ProgressBar progress={task.progress} color="primary" size="sm" />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm">
                        <Edit3 size={14} className="mr-2" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm">
                        <MessageSquare size={14} className="mr-2" />
                        Comment
                      </Button>
                      {task.status !== 'completed' && (
                        <Button size="sm">
                          <CheckCircle size={14} className="mr-2" />
                          Mark Complete
                        </Button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {selectedView === 'documents' && (
          <motion.div
            key="documents"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Shared Documents</h3>
                <Button size="sm">
                  <Upload size={16} className="mr-2" />
                  Upload File
                </Button>
              </div>

              <div className="space-y-4">
                {sharedDocuments.map((doc, index) => (
                  <motion.div
                    key={doc.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <FileText className="text-primary-600" size={20} />
                    </div>

                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{doc.name}</h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Shared by {familyMembers.find(m => m.id === doc.sharedBy)?.name}</span>
                        <span>{doc.lastModified}</span>
                        <span>{doc.size}</span>
                        <Badge variant="secondary" size="sm">{doc.permissions}</Badge>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm">
                        <Eye size={14} />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Download size={14} />
                      </Button>
                      {doc.permissions === 'edit' && (
                        <Button variant="ghost" size="sm">
                          <Edit3 size={14} />
                        </Button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {selectedView === 'meetings' && (
          <motion.div
            key="meetings"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-6"
          >
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Meetings</h3>
                <Button size="sm">
                  <Calendar size={16} className="mr-2" />
                  Schedule Meeting
                </Button>
              </div>

              <div className="space-y-4">
                {upcomingMeetings.map((meeting, index) => (
                  <motion.div
                    key={meeting.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border border-gray-200 rounded-xl p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">{meeting.title}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                          <div className="flex items-center space-x-1">
                            <Calendar size={14} />
                            <span>{new Date(meeting.date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock size={14} />
                            <span>{meeting.time} ({meeting.duration} min)</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            {meeting.type === 'video' ? <Video size={14} /> : <Phone size={14} />}
                            <span>{meeting.type}</span>
                          </div>
                        </div>

                        <div className="mb-4">
                          <h5 className="font-medium text-gray-800 mb-2">Participants:</h5>
                          <div className="flex space-x-2">
                            {meeting.participants.map(participantId => {
                              const participant = familyMembers.find(m => m.id === participantId);
                              return (
                                <div key={participantId} className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full">
                                  <span className="text-sm">{participant?.avatar}</span>
                                  <span className="text-sm font-medium">{participant?.name}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <h5 className="font-medium text-gray-800 mb-2">Agenda:</h5>
                          <ul className="space-y-1">
                            {meeting.agenda.map((item, idx) => (
                              <li key={idx} className="text-sm text-gray-600 flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-primary-400 rounded-full" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button size="sm">
                        <Video size={14} className="mr-2" />
                        Join Meeting
                      </Button>
                      <Button variant="outline" size="sm">
                        <Edit3 size={14} className="mr-2" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm">
                        <Share2 size={14} className="mr-2" />
                        Share
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};